import {
  CodeIcon,
  CommentDiscussionIcon,
  DiffIcon,
  FileDirectoryFillIcon,
  FileIcon,
  GitCommitIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueOpenedIcon,
  LinkIcon,
  RepoIcon,
  SkipIcon,
  SpaceIcon,
  TagIcon,
  WorkflowIcon,
} from '@primer/octicons-react'

import styles from './ChatComposer.module.css'

/**
 * Icon variants a composer attachment token can render. Beyond the plain
 * file/folder/repo kinds, issue and pull-request tokens carry their state so the
 * token mirrors the status glyph shown elsewhere in Copilot Chat.
 */
export type ComposerAttachmentIcon =
  | 'file'
  | 'folder'
  | 'repo'
  | 'commit'
  | 'discussion'
  | 'diff'
  | 'release'
  | 'snippet'
  | 'link'
  | 'workflow'
  | 'issue'
  | 'issue-open'
  | 'issue-closed'
  | 'issue-not-planned'
  | 'pull'
  | 'pull-open'
  | 'pull-closed'
  | 'pull-merged'
  | 'pull-draft'
  | 'space'

export const ATTACHMENT_ICONS = {
  file: FileIcon,
  folder: FileDirectoryFillIcon,
  repo: RepoIcon,
  commit: GitCommitIcon,
  discussion: CommentDiscussionIcon,
  diff: DiffIcon,
  release: TagIcon,
  snippet: CodeIcon,
  link: LinkIcon,
  workflow: WorkflowIcon,
  issue: IssueOpenedIcon,
  'issue-open': IssueOpenedIcon,
  'issue-closed': IssueClosedIcon,
  'issue-not-planned': SkipIcon,
  pull: GitPullRequestIcon,
  'pull-open': GitPullRequestIcon,
  'pull-closed': GitPullRequestClosedIcon,
  'pull-merged': GitMergeIcon,
  'pull-draft': GitPullRequestDraftIcon,
  space: SpaceIcon,
} as const satisfies Record<ComposerAttachmentIcon, typeof FileIcon>

/**
 * State-based icon tint classes, mirroring Copilot Chat's reference tokens
 * (open → open, issue-closed/merged → done, pull-closed → danger). Variants
 * without an entry (including the neutral `issue`/`pull` fallbacks for unknown
 * state) keep the default muted color.
 */
export const ATTACHMENT_ICON_COLORS: Partial<Record<ComposerAttachmentIcon, string>> = {
  'issue-open': styles.AttachmentIconOpen,
  'issue-closed': styles.AttachmentIconDone,
  'pull-open': styles.AttachmentIconOpen,
  'pull-closed': styles.AttachmentIconDanger,
  'pull-merged': styles.AttachmentIconDone,
}

/**
 * Human-readable label conveyed by each attachment icon. Rendered as
 * visually-hidden text so screen-reader users receive the type (and, for
 * issue/PR variants, the status) that sighted users get from the glyph + color
 * (which octicons expose as `aria-hidden`). Every icon variant has an entry so
 * no attachment relies on the glyph alone to convey its type.
 */
export const ATTACHMENT_ICON_STATUS_LABELS: Partial<Record<ComposerAttachmentIcon, string>> = {
  file: 'File',
  folder: 'Folder',
  repo: 'Repository',
  commit: 'Commit',
  discussion: 'Discussion',
  diff: 'File diff',
  release: 'Release',
  snippet: 'Code snippet',
  link: 'Link',
  workflow: 'Workflow job',
  issue: 'Issue',
  'issue-open': 'Open issue',
  'issue-closed': 'Closed issue',
  'issue-not-planned': 'Issue (not planned)',
  pull: 'Pull request',
  'pull-open': 'Open pull request',
  'pull-closed': 'Closed pull request',
  'pull-merged': 'Merged pull request',
  'pull-draft': 'Draft pull request',
}
